import React, { useState, useEffect, useRef } from 'react';
import { api } from '../config/api';
import toast from 'react-hot-toast';

interface HOADoc {
  name: string;
  type: string;
  path: string;
  uploaded_at?: string;
}

interface HOAAssociation {
  name: string;
  type: string;
  management?: string;
  monthly: number;
  annual?: number;
  payment?: string;
  portal?: string;
  account?: string;
  portalUrl?: string;
  notes?: string;
  documents?: HOADoc[];
  portal_check?: {
    balance: number;
    status: string;
    checked_at: string;
    error_message?: string;
    violation_amount?: number;
  };
}

interface PropertyHOA {
  address: string;
  region?: string;
  hoas: HOAAssociation[];
}

interface HOATransaction {
  id: number; date: string; merchant: string; amount: number; account: string; tags: string;
}

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const HOAPortal: React.FC = () => {
  const [properties, setProperties] = useState<PropertyHOA[]>([]);
  const [transactions, setTransactions] = useState<HOATransaction[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'associations' | 'table' | 'laws' | 'transactions'>('associations');
  const [previewDoc, setPreviewDoc] = useState<{url: string; name: string} | null>(null);
  const [uploadTarget, setUploadTarget] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadAssociations(); loadTransactions(); }, []);

  const loadAssociations = async () => {
    try { const res = await api.get('/hoa-checker/associations'); setProperties(res.data.properties || []); } catch {}
  };
  const loadTransactions = async () => {
    try { const res = await api.get('/hoa-sync/transactions', { params: { limit: 200 } }); setTransactions(res.data.transactions || []); setTotalTransactions(res.data.total || 0); } catch {}
  };
  const syncTransactions = async () => {
    setIsSyncing(true); toast.loading('Syncing...', { id: 'sync' });
    try { const res = await api.post('/hoa-sync/pull-transactions'); toast.success(`${res.data.inserted} new`, { id: 'sync' }); await loadTransactions(); }
    catch (err: any) { toast.error(err.response?.data?.detail || 'Failed', { id: 'sync' }); }
    finally { setIsSyncing(false); }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !uploadTarget) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('hoa_name', uploadTarget);
    formData.append('doc_type', 'bylaws');
    toast.loading('Uploading...', { id: 'upload' });
    try {
      await api.post('/hoa-checker/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${file.name}`, { id: 'upload' });
      await loadAssociations();
    } catch (err: any) { toast.error('Upload failed', { id: 'upload' }); }
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deleteDoc = async (hoaName: string, filename: string) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try { await api.delete('/hoa-checker/documents', { params: { hoa_name: hoaName, filename } }); toast.success('Deleted'); await loadAssociations(); }
    catch { toast.error('Delete failed'); }
  };

  // Computed
  const allHoas = properties.flatMap(p => p.hoas.map(h => ({ ...h, address: p.address, region: p.region })));
  const totalMonthly = allHoas.reduce((s, h) => s + (h.monthly || 0), 0);
  const totalAnnual = allHoas.reduce((s, h) => s + (h.annual || h.monthly * 12 || 0), 0);
  const totalHOACount = allHoas.length;
  const allDocs = allHoas.flatMap(h => (h.documents || []).map(d => ({ ...d, hoaName: h.name, hoaType: h.type, address: h.address })));

  const attentionProps = properties.filter(p => p.hoas.some(h => h.notes || (h.portal_check && (h.portal_check.balance > 0 || h.portal_check.status === 'error'))));
  const sortedProps = [...properties].sort((a, b) => b.hoas.reduce((s, h) => s + h.monthly, 0) - a.hoas.reduce((s, h) => s + h.monthly, 0));

  const byRegion: Record<string, PropertyHOA[]> = {};
  for (const p of sortedProps) { const r = p.region || 'Other'; if (!byRegion[r]) byRegion[r] = []; byRegion[r].push(p); }

  const propStatus = (p: PropertyHOA) => {
    if (p.hoas.some(h => h.portal_check?.status === 'error')) return 'red';
    if (p.hoas.some(h => h.notes || (h.portal_check && h.portal_check.balance > 0))) return 'yellow';
    return 'green';
  };

  const statusDotClass = (c: string) => c === 'green' ? 'bg-green-400' : c === 'yellow' ? 'bg-yellow-400' : c === 'red' ? 'bg-red-400' : 'bg-gray-500';
  const monthlyColor = (a: number) => a >= 500 ? 'text-red-400' : a >= 100 ? 'text-yellow-300' : 'text-green-400';
  const typeBadge = (t: string) => {
    switch (t.toLowerCase()) {
      case 'master': return 'bg-indigo-500/20 text-indigo-400 border-indigo-600/30';
      case 'main': return 'bg-cyan-500/20 text-cyan-400 border-cyan-600/30';
      case 'neighborhood': return 'bg-emerald-500/20 text-emerald-400 border-emerald-600/30';
      case 'unit owners': return 'bg-purple-500/20 text-purple-400 border-purple-600/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-600/30';
    }
  };

  const merchantSummary = transactions.reduce((acc, t) => {
    const m = t.merchant; if (!acc[m]) acc[m] = { count: 0, total: 0, lastDate: '' };
    acc[m].count++; acc[m].total += t.amount; if (t.date > acc[m].lastDate) acc[m].lastDate = t.date; return acc;
  }, {} as Record<string, { count: number; total: number; lastDate: string }>);

  const hoaLink = (hoa: HOAAssociation) => hoa.portalUrl ? (
    <a href={hoa.portalUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors">{hoa.name}</a>
  ) : <span className="text-gray-200">{hoa.name}</span>;

  // Unique HOAs for the Laws tab (deduplicated by name)
  const uniqueHoas = Array.from(new Map(allHoas.map(h => [h.name, h])).values()).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-5 pb-8">
      <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.txt" onChange={handleUpload} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-3">
            <span className="text-cyan-400">~/</span>hoa-portal
          </h1>
          <p className="text-gray-500 text-sm mt-1 font-mono">HOA associations, portal balances &amp; regulations</p>
        </div>
        <button onClick={syncTransactions} disabled={isSyncing}
          className="text-xs font-mono bg-gray-800/50 text-gray-400 px-3 py-1.5 rounded border border-gray-700/50 hover:border-gray-600 transition-colors disabled:opacity-40">
          {isSyncing ? 'Syncing...' : 'Sync Transactions'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Monthly Total</div>
          <div className="text-xl font-bold font-mono mt-1 text-yellow-400">{fmt(totalMonthly)}</div>
          <div className="text-gray-600 text-[10px] font-mono">{fmt(totalAnnual)}/yr</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Properties</div>
          <div className="text-xl font-bold font-mono mt-1 text-white">{properties.length}</div>
          <div className="text-gray-600 text-[10px] font-mono">{totalHOACount} associations</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Need Attention</div>
          <div className={`text-xl font-bold font-mono mt-1 ${attentionProps.length > 0 ? 'text-yellow-400' : 'text-green-400'}`}>{attentionProps.length}</div>
        </div>
        <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-4">
          <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Documents</div>
          <div className="text-xl font-bold font-mono mt-1 text-white">{allDocs.length}</div>
          <div className="text-gray-600 text-[10px] font-mono">bylaws, CC&amp;Rs, rules</div>
        </div>
      </div>

      {/* Attention */}
      {attentionProps.length > 0 && (
        <div className="bg-yellow-900/10 border border-yellow-700/20 rounded-lg p-4">
          <div className="text-yellow-400 text-xs font-mono font-bold uppercase tracking-wider mb-3">Needs Attention</div>
          {attentionProps.map(p => (
            <div key={`att-${p.address}`} className="flex items-center gap-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
              <span className="text-gray-300 text-sm font-mono flex-1">{p.address}</span>
              {p.hoas.filter(h => h.notes || (h.portal_check && (h.portal_check.balance > 0 || h.portal_check.status === 'error'))).map((h, i) => (
                <span key={i} className="text-yellow-400/80 text-xs font-mono">
                  {h.notes || (h.portal_check?.status === 'error' ? `${h.name}: error` : `${h.name}: ${fmt(h.portal_check?.balance || 0)} due`)}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-800">
        {([
          { key: 'associations', label: `By Region (${properties.length})` },
          { key: 'table', label: `Table View (${totalHOACount})` },
          { key: 'laws', label: `Laws & Regulations (${allDocs.length})` },
          { key: 'transactions', label: `Transactions (${totalTransactions})` },
        ] as const).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as any)}
            className={`pb-2 px-1 text-sm font-mono transition-colors ${activeTab === tab.key ? 'text-cyan-400 border-b-2 border-cyan-400' : 'text-gray-500 hover:text-gray-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== BY REGION TAB ===== */}
      {activeTab === 'associations' && (
        <div className="space-y-6">
          {Object.entries(byRegion).map(([region, props]) => {
            const regionTotal = props.reduce((s, p) => s + p.hoas.reduce((s2, h) => s2 + h.monthly, 0), 0);
            return (
              <div key={region}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-yellow-500/70 text-xs font-mono font-bold uppercase tracking-wider">{region}</span>
                  <span className="flex-1 h-px bg-gray-800" />
                  <span className="text-gray-500 text-xs font-mono">{props.length} properties &middot; {fmt(regionTotal)}/mo</span>
                </div>
                <div className="space-y-2">
                  {props.map(prop => {
                    const propTotal = prop.hoas.reduce((s, h) => s + h.monthly, 0);
                    const status = propStatus(prop);
                    return (
                      <div key={prop.address} className="bg-gray-800/30 border border-gray-700/30 rounded-lg overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-gray-700/20 flex items-center gap-3">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusDotClass(status)}`} />
                          <span className="font-mono text-sm font-semibold text-white flex-1">{prop.address}</span>
                          <span className={`font-mono text-sm font-bold ${monthlyColor(propTotal)}`}>{fmt(propTotal)}</span>
                          <span className="text-gray-600 text-xs font-mono">/mo</span>
                          <span className="text-gray-600 text-xs font-mono hidden md:inline">({fmt(propTotal * 12)}/yr)</span>
                        </div>
                        {prop.hoas.map((hoa, i) => (
                          <div key={i} className="px-4 py-2 flex items-center gap-3 border-b border-gray-800/20 last:border-b-0 hover:bg-gray-800/20 text-sm">
                            <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border w-[90px] text-center flex-shrink-0 ${typeBadge(hoa.type)}`}>{hoa.type}</span>
                            <div className="flex-1 min-w-0">
                              <span className="font-mono">{hoaLink(hoa)}</span>
                              {hoa.management && <span className="text-gray-600 text-xs ml-2 hidden lg:inline">({hoa.management})</span>}
                              {hoa.notes && <span className="text-yellow-400 text-xs ml-2 bg-yellow-900/30 px-1.5 py-0.5 rounded">{hoa.notes}</span>}
                            </div>
                            <span className={`font-mono text-sm font-semibold w-[85px] text-right ${monthlyColor(hoa.monthly)}`}>{hoa.monthly > 0 ? fmt(hoa.monthly) : '-'}</span>
                            {hoa.portal_check ? (
                              <span className={`font-mono text-xs w-[75px] text-right ${hoa.portal_check.balance > 0 ? 'text-yellow-400' : hoa.portal_check.status === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                                {hoa.portal_check.status === 'error' ? 'ERR' : fmt(hoa.portal_check.balance)}
                              </span>
                            ) : <span className="w-[75px]" />}
                            {hoa.portal ? (
                              <span className="text-[8px] uppercase tracking-wider text-indigo-400/70 bg-indigo-500/10 px-1.5 py-0.5 rounded w-[70px] text-center flex-shrink-0">{hoa.portal}</span>
                            ) : <span className="w-[70px] flex-shrink-0" />}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== TABLE VIEW TAB ===== */}
      {activeTab === 'table' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead>
              <tr className="text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700">
                <th className="text-left py-2 px-2">Property</th>
                <th className="text-left py-2 px-2">HOA Name</th>
                <th className="text-center py-2 px-2 w-[80px]">Type</th>
                <th className="text-right py-2 px-2">Monthly</th>
                <th className="text-right py-2 px-2">Annual</th>
                <th className="text-right py-2 px-2">Portal Bal</th>
                <th className="text-center py-2 px-2">Status</th>
                <th className="text-left py-2 px-2">Payment</th>
              </tr>
            </thead>
            <tbody>
              {sortedProps.flatMap(prop =>
                prop.hoas.map((hoa, i) => {
                  const check = hoa.portal_check;
                  const annual = hoa.annual || hoa.monthly * 12;
                  return (
                    <tr key={`${prop.address}-${i}`} className="border-b border-gray-800/30 hover:bg-gray-800/20">
                      <td className="py-1.5 px-2 text-gray-300">{i === 0 ? prop.address : ''}</td>
                      <td className="py-1.5 px-2">
                        {hoaLink(hoa)}
                        {hoa.notes && <span className="text-yellow-400 text-[10px] ml-1">*</span>}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <span className={`text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${typeBadge(hoa.type)}`}>{hoa.type}</span>
                      </td>
                      <td className={`py-1.5 px-2 text-right font-semibold ${monthlyColor(hoa.monthly)}`}>{hoa.monthly > 0 ? fmt(hoa.monthly) : '-'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-500">{annual > 0 ? fmt(annual) : '-'}</td>
                      <td className={`py-1.5 px-2 text-right ${check ? (check.balance > 0 ? 'text-yellow-400 font-semibold' : check.status === 'error' ? 'text-red-400' : 'text-green-400') : 'text-gray-700'}`}>
                        {check ? (check.status === 'error' ? 'ERR' : fmt(check.balance)) : '-'}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${check ? (check.status === 'current' ? 'bg-green-400' : check.status === 'error' ? 'bg-red-400' : 'bg-yellow-400') : 'bg-gray-700'}`} />
                      </td>
                      <td className="py-1.5 px-2 text-gray-600 text-xs truncate max-w-[150px]">{hoa.payment || ''}</td>
                    </tr>
                  );
                })
              )}
              <tr className="border-t-2 border-yellow-600/30 bg-yellow-900/10">
                <td className="py-2 px-2 text-yellow-400 font-bold" colSpan={3}>TOTAL ({properties.length} properties, {totalHOACount} HOAs)</td>
                <td className="py-2 px-2 text-right text-yellow-400 font-bold">{fmt(totalMonthly)}</td>
                <td className="py-2 px-2 text-right text-yellow-400 font-bold">{fmt(totalAnnual)}</td>
                <td colSpan={3}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ===== LAWS & REGULATIONS TAB ===== */}
      {activeTab === 'laws' && (
        <div className="space-y-4">
          <p className="text-gray-500 text-xs font-mono">Upload CC&amp;Rs, bylaws, rules &amp; regulations, and other governing documents for each HOA. Click a document to preview inline.</p>

          {uniqueHoas.map(hoa => {
            const docs = hoa.documents || [];
            return (
              <div key={hoa.name} className="bg-gray-800/30 border border-gray-700/30 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-700/20 flex items-center gap-3">
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border w-[90px] text-center flex-shrink-0 ${typeBadge(hoa.type)}`}>{hoa.type}</span>
                  <div className="flex-1">
                    <span className="font-mono text-sm text-white">{hoaLink(hoa)}</span>
                    {hoa.management && <span className="text-gray-600 text-xs ml-2">({hoa.management})</span>}
                  </div>
                  <span className="text-gray-500 text-xs font-mono">{docs.length} doc{docs.length !== 1 ? 's' : ''}</span>
                  <button
                    onClick={() => { setUploadTarget(hoa.name); fileInputRef.current?.click(); }}
                    className="text-[10px] font-mono text-cyan-400 bg-cyan-900/20 px-2 py-1 rounded border border-cyan-700/30 hover:bg-cyan-800/30 transition-colors"
                  >
                    + Upload
                  </button>
                </div>

                {docs.length > 0 ? (
                  <div className="divide-y divide-gray-800/30">
                    {docs.map((doc, i) => (
                      <div key={i} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-800/20">
                        <span className="text-red-400 text-xs flex-shrink-0">PDF</span>
                        <button
                          onClick={() => setPreviewDoc({ url: doc.path, name: doc.name })}
                          className="text-gray-300 text-sm font-mono hover:text-cyan-400 transition-colors flex-1 text-left truncate"
                        >
                          {doc.name}
                        </button>
                        <span className="text-gray-600 text-[10px] font-mono hidden md:inline">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}</span>
                        <a href={doc.path} download className="text-gray-500 hover:text-gray-300 text-xs font-mono transition-colors">Download</a>
                        <button onClick={() => deleteDoc(hoa.name, doc.name)} className="text-gray-600 hover:text-red-400 text-xs transition-colors">Delete</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-gray-600 text-xs font-mono text-center">
                    No documents uploaded yet. Upload CC&amp;Rs, bylaws, or rules.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== TRANSACTIONS TAB ===== */}
      {activeTab === 'transactions' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(merchantSummary).sort(([, a], [, b]) => a.total - b.total).map(([merchant, data]) => (
              <div key={merchant} className="bg-gray-800/30 border border-gray-700/30 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <div className="text-gray-300 text-xs font-mono truncate max-w-[200px]">{merchant}</div>
                  <div className="text-gray-600 text-[10px] font-mono">{data.count} payments &middot; Last: {data.lastDate}</div>
                </div>
                <div className="text-red-400 font-mono text-sm font-semibold">{fmt(Math.abs(data.total))}</div>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-mono">
              <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Payee</th>
                <th className="text-right py-2 px-3">Amount</th><th className="text-left py-2 px-3">Account</th><th className="text-left py-2 px-3">Tags</th>
              </tr></thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="py-2 px-3 text-gray-400">{t.date}</td>
                    <td className="py-2 px-3 text-gray-300">{t.merchant}</td>
                    <td className="py-2 px-3 text-right text-red-400 font-semibold">{fmt(Math.abs(t.amount))}</td>
                    <td className="py-2 px-3 text-gray-500 text-xs">{t.account}</td>
                    <td className="py-2 px-3 text-cyan-600 text-xs">{t.tags || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== PDF PREVIEW MODAL ===== */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewDoc(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <span className="text-white font-mono text-sm">{previewDoc.name}</span>
              <div className="flex gap-2">
                <a href={previewDoc.url} download className="text-xs font-mono text-cyan-400 bg-cyan-900/20 px-3 py-1 rounded border border-cyan-700/30 hover:bg-cyan-800/30">Download</a>
                <button onClick={() => setPreviewDoc(null)} className="text-gray-400 hover:text-white text-lg px-2">&times;</button>
              </div>
            </div>
            <iframe src={previewDoc.url} className="flex-1 w-full bg-white rounded-b-xl" title="Document Preview" />
          </div>
        </div>
      )}
    </div>
  );
};

export default HOAPortal;
